import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiErrorDetail } from "../lib/api";

export default function ShopProduct() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQty = Math.max(1, Math.min(10, parseInt(searchParams.get("qty") || "1", 10) || 1));
  const initialSize = searchParams.get("size") || "";
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(initialQty);
  const [shipping, setShipping] = useState({
    name: "",
    email: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    postcode: "",
    country: "United Kingdom",
  });
  const [busy, setBusy] = useState(false);
  const [step, setStep] = useState(initialSize ? "address" : "select");

  useEffect(() => {
    api
      .get(`/shop/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        if (initialSize && r.data.sizes.includes(initialSize)) {
          setSize(initialSize);
        } else {
          setSize(r.data.sizes[0] || "");
        }
      })
      .catch(() => navigate("/shop"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const update = (k, v) => setShipping((s) => ({ ...s, [k]: v }));

  const checkout = async (e) => {
    e.preventDefault();
    if (!size) {
      toast.error("Please pick a size.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/shop/checkout", {
        items: [{ product_id: product.id, size, quantity: qty }],
        shipping,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Checkout failed");
    } finally {
      setBusy(false);
    }
  };

  if (!product) {
    return <div className="px-6 md:px-12 lg:px-20 py-32 font-mono text-sm text-[#71717A]">Loading…</div>;
  }

  const subtotal = (product.price_gbp * qty).toFixed(2);
  const total = (product.price_gbp * qty + 3.99).toFixed(2);

  return (
    <div data-testid="shop-product-page" className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
      <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-[#71717A] hover:text-black mb-8">
        <ArrowLeft size={14} /> Back to shop
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* IMAGE */}
        <div className="lg:col-span-7">
          <div className="lg:sticky lg:top-28 border border-black/10 bg-white overflow-hidden">
            <img src={resolveImage(product.image_url)} alt={product.title} loading="eager" style={{ filter: "contrast(1.04) saturate(1.06)" }} className="w-full h-auto object-cover" />
          </div>
        </div>

        {/* DETAIL */}
        <div className="lg:col-span-5">
          <h1 className="font-display font-black text-5xl md:text-6xl leading-[0.9] mb-4">{product.title}</h1>
          <p className="font-display font-black text-3xl text-[#E6007A] mb-6">£{product.price_gbp.toFixed(2)}</p>
          <p className="font-mono text-base leading-relaxed text-[#27272A] mb-8">{product.description || "Limited DTF print drop, made in our Cornwall studio."}</p>

          {/* Shop closed notice — replaces the size/address flow */}
          <div data-testid="shop-closed-notice" className="border-2 border-black bg-[#FFD600] p-6 md:p-8">
            <p className="font-display font-black text-2xl md:text-3xl leading-tight mb-3">
              The shop isn't taking orders yet.
            </p>
            <p className="font-mono text-sm leading-relaxed text-black mb-5">
              We're still finishing the launch. Want this piece when we go live? Drop us a message and we'll add you to the early-access list.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/contact"
                data-testid="closed-go-contact"
                className="btn-ink bg-black text-white px-5 h-11 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center"
              >
                Send a message →
              </Link>
              <a
                href="https://www.instagram.com/shopxemi/"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-black border border-black px-5 h-11 font-mono text-xs uppercase tracking-[0.2em] inline-flex items-center"
              >
                Follow on Instagram
              </a>
            </div>
          </div>

          {false && step === "select" ? (
            <div className="space-y-6 border-t border-black/10 pt-8">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">Size</label>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      type="button"
                      data-testid={`size-${s}`}
                      onClick={() => setSize(s)}
                      className={`px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] border transition-all ${
                        size === s ? "bg-black text-white border-black" : "bg-white text-black border-black/15 hover:border-black"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">Quantity</label>
                <div className="flex items-center border border-black/15 w-fit">
                  <button type="button" onClick={() => setQty(Math.max(1, qty - 1))} className="w-12 h-12 font-mono text-lg hover:bg-black hover:text-white" data-testid="qty-minus">−</button>
                  <span className="w-12 h-12 inline-flex items-center justify-center font-mono text-base" data-testid="qty-display">{qty}</span>
                  <button type="button" onClick={() => setQty(Math.min(10, qty + 1))} className="w-12 h-12 font-mono text-lg hover:bg-black hover:text-white" data-testid="qty-plus">+</button>
                </div>
              </div>
              <div className="border-t border-black/10 pt-6 space-y-1 font-mono text-sm">
                <p className="flex justify-between"><span>Subtotal</span><span>£{subtotal}</span></p>
                <p className="flex justify-between text-[#71717A]"><span>UK shipping</span><span>£3.99</span></p>
                <p className="flex justify-between font-display font-black text-xl text-black mt-3"><span>Total</span><span>£{total}</span></p>
              </div>
              <button
                onClick={() => setStep("address")}
                data-testid="proceed-to-address"
                className="btn-ink-magenta w-full bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag size={14} /> Continue to delivery →
              </button>
            </div>
          ) : (
            <form onSubmit={checkout} data-testid="address-form" className="space-y-4 border-t border-black/10 pt-8">
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717A]">Delivery details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full name" value={shipping.name} onChange={(v) => update("name", v)} required testid="ship-name" />
                <Field label="Email" type="email" value={shipping.email} onChange={(v) => update("email", v)} required testid="ship-email" />
              </div>
              <Field label="Phone (optional)" value={shipping.phone} onChange={(v) => update("phone", v)} testid="ship-phone" />
              <Field label="Address line 1" value={shipping.address_line1} onChange={(v) => update("address_line1", v)} required testid="ship-addr1" />
              <Field label="Address line 2 (optional)" value={shipping.address_line2} onChange={(v) => update("address_line2", v)} testid="ship-addr2" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="City" value={shipping.city} onChange={(v) => update("city", v)} required testid="ship-city" />
                <Field label="Postcode" value={shipping.postcode} onChange={(v) => update("postcode", v)} required testid="ship-postcode" />
              </div>
              <Field label="Country" value={shipping.country} onChange={(v) => update("country", v)} required testid="ship-country" />

              <div className="border-t border-black/10 pt-5 space-y-1 font-mono text-sm">
                <p className="flex justify-between"><span>{product.title} ({size}) × {qty}</span><span>£{subtotal}</span></p>
                <p className="flex justify-between text-[#71717A]"><span>UK shipping</span><span>£3.99</span></p>
                <p className="flex justify-between font-display font-black text-xl text-black mt-3"><span>Total</span><span>£{total}</span></p>
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep("select")} className="bg-white text-black border border-black px-6 py-4 font-mono text-xs uppercase tracking-[0.25em]" data-testid="back-to-select">← Back</button>
                <button type="submit" disabled={busy} data-testid="pay-now" className="btn-ink flex-1 bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50">
                  {busy ? "Redirecting to Stripe…" : `Pay £${total} →`}
                </button>
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] mt-3 text-center">
                Secure card payment via Stripe · Apple/Google Pay supported
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
        {label}{required && " *"}
      </label>
      <input
        type={type}
        required={required}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black focus:ring-2 focus:ring-black/10 rounded-none"
      />
    </div>
  );
}
